import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProposalDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, FilterPill, Panel, type Tone } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { openJarvis } from "@/jarvis/jarvis-store";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "../decisions/project-filter";
import { ConfirmModal, InboxEmptyRow, InboxShell, useInboxToast } from "../inbox/InboxShell";

const urgencyTone: Record<string, Tone> = {
  URGENT: "red",
  HIGH: "amber",
  NORMAL: "cyan",
  LOW: "dim",
};

/**
 * Návrhy AI — vlastní obrazovka nad modelem `Proposal`.
 *
 * Dosud se návrh dostal před ownera jen tak, že si vyrobil rozhodnutí; v jedné
 * schránce se pak potkal „vyber platební bránu" s „napadlo mě přidat export".
 * Návrhy založené postaru mají `decisionId` — čtou se odsud stejně jako nové
 * a jejich vyřízením se zavře i to připojené rozhodnutí, takže nikde nezůstane
 * viset duch.
 */
export function ProposalsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showDecided, setShowDecided] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { toast, showToast } = useInboxToast();
  const { project: rawProjectFilter = ALL_PROJECTS } = useSearch({ from: "/shell/proposals" });

  const query = useQuery({
    queryKey: ["proposals-inbox", showDecided],
    queryFn: () => api.proposalInbox(showDecided ? { status: "DECIDED" } : {}),
  });

  const undoMutation = useMutation({
    mutationFn: (id: string) => api.reopenProposal(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["proposals-inbox"] });
      showToast(cs.proposals.undoneToast);
    },
    onError: (err) => showToast(err instanceof Error ? err.message : cs.common.error),
  });

  const allItems = query.data?.items ?? [];
  const projectFilter = resolveProjectFilter(projectGroups(allItems), rawProjectFilter);
  const items = filterByProject(allItems, projectFilter);

  return (
    <InboxShell
      testId="proposals"
      title={cs.proposals.title}
      kicker="NÁVRHY"
      lead={cs.proposals.lead(allItems.filter((p) => p.status === "PENDING").length)}
      right={
        <div className="flex flex-wrap items-center gap-2">
          <ProjectFilterTabs
            items={allItems}
            value={projectFilter}
            onChange={(key) => {
              void navigate({
                to: "/proposals",
                search: key === ALL_PROJECTS ? {} : { project: key },
              });
              setSelectedId(null);
            }}
            testIdPrefix="proposals"
          />
          <FilterPill
            active={showDecided}
            onClick={() => {
              setShowDecided((v) => !v);
              setSelectedId(null);
            }}
            testId="proposals-toggle-decided"
          >
            {showDecided ? cs.proposals.hideDecided : cs.proposals.showDecided}
          </FilterPill>
        </div>
      }
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      emptyList={<InboxEmptyRow title={cs.proposals.emptyTitle} note={cs.proposals.emptyNote} />}
      emptyDetail={cs.proposals.emptyDetail}
      emptyDetailNote={cs.proposals.emptyDetailNote}
      toast={toast}
      onUndo={(id) => undoMutation.mutate(id)}
      undoPending={undoMutation.isPending}
      renderRow={(p) => (
        <>
          <div className="af-mono flex justify-between text-[12px] tracking-[0.1em]">
            <span
              className="font-bold"
              style={{
                color:
                  p.status !== "PENDING"
                    ? "var(--positive)"
                    : urgencyTone[p.urgency] === "red"
                      ? "var(--critical)"
                      : urgencyTone[p.urgency] === "amber"
                        ? "var(--warning)"
                        : "var(--accent)",
              }}
            >
              {p.status === "PENDING" ? p.urgency : cs.proposals.decidedTag}
            </span>
            <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(p.createdAt)}</span>
          </div>
          <div className="af-card-title mb-[3px] mt-[5px] leading-snug">{p.title}</div>
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {p.projectName} · {p.proposedByLabel}
          </div>
        </>
      )}
      renderDetail={(p) => (
        <ProposalDetail
          key={p.id}
          item={p}
          onDecided={(msg, undoId) => {
            void queryClient.invalidateQueries({ queryKey: ["proposals-inbox"] });
            void queryClient.invalidateQueries({ queryKey: ["inbox"] });
            void queryClient.invalidateQueries({ queryKey: ["projects"] });
            showToast(msg, undoId);
            setSelectedId(null);
          }}
        />
      )}
    />
  );
}

function ProposalDetail({
  item,
  onDecided,
}: {
  item: ProposalDTO;
  onDecided: (msg: string, undoId?: string) => void;
}) {
  const { session } = useAuth();
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<"APPROVE" | "REJECT" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decideMutation = useMutation({
    mutationFn: (decision: "APPROVE" | "REJECT") =>
      api.decideProposal(item.id, {
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (data, decision) =>
      // Schválený návrh vrátit nejde — vznikl z něj task, který už může být
      // rozpracovaný. Undo se proto nabízí jen u zamítnutí.
      onDecided(
        decision === "APPROVE"
          ? cs.proposals.approvedToast(data.approvedTaskNumber)
          : cs.proposals.rejectedToast,
        decision === "APPROVE" ? undefined : item.id,
      ),
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const canDecide = session?.user.role !== "CLIENT";
  const urgTone = urgencyTone[item.urgency] ?? "cyan";

  return (
    <div className="max-w-[860px]" data-testid="proposals-detail">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="af-mono flex flex-wrap items-center gap-2 text-[11px]">
          <Chip tone={item.status === "PENDING" ? urgTone : "green"}>
            {item.status === "PENDING" ? item.urgency : cs.proposals.decidedTag}
          </Chip>
          <span style={{ color: "var(--text-tertiary)" }}>
            {item.projectName} · {relativeTime(item.createdAt)} · {cs.proposals.proposedBy}{" "}
            {item.proposedByLabel}
          </span>
        </div>
        <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          NÁVRH #{item.id.slice(-5)}
        </span>
      </div>
      <h2
        className="af-display mb-5 mt-2 text-[26px] font-bold leading-tight"
        style={{ color: "var(--text-primary)" }}
      >
        {item.title}
      </h2>

      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.proposals.what}
          </div>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.description || "—"}
          </p>
        </Panel>
        <Panel className="p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.proposals.why}
          </div>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.rationale || "—"}
          </p>
        </Panel>
      </div>

      {item.decisionId && item.status === "PENDING" ? (
        <div className="mb-3 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.proposals.legacyDecision}
        </div>
      ) : null}

      {item.status !== "PENDING" ? (
        <Panel tone="green" className="mb-3 p-4">
          <div className="af-label mb-1.5" style={{ color: "var(--positive)" }}>
            {item.status === "APPROVED" ? "SCHVÁLENO" : "ZAMÍTNUTO"}
          </div>
          <div className="text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.decidedNote || "—"}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {item.decidedByLabel ?? "?"} · {item.decidedAt ? relativeTime(item.decidedAt) : ""}
          </div>
          {item.approvedTaskNumber && item.projectSlug ? (
            <Link
              to="/projects/$slug/tasks/$number"
              params={{ slug: item.projectSlug, number: String(item.approvedTaskNumber) }}
              className="mt-2 block text-[12px]"
            >
              #{item.approvedTaskNumber} →
            </Link>
          ) : null}
        </Panel>
      ) : (
        <>
          <Panel className="mb-4 p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.proposals.noteLabel}
            </div>
            <textarea
              className="w-full resize-y bg-transparent px-3 py-2 text-[12px] outline-none"
              style={{
                borderRadius: "var(--radius-box)",
                border: "1px solid var(--border-default)",
                color: "var(--text-primary)",
                minHeight: 64,
              }}
              placeholder={cs.proposals.notePlaceholder}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="proposal-note"
            />
          </Panel>

          {error ? (
            <div className="mb-2 text-[12px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2.5">
            <Btn
              variant="primary"
              size="lg"
              disabled={decideMutation.isPending || !canDecide}
              onClick={() => {
                setError(null);
                setConfirming("APPROVE");
              }}
              data-testid="proposal-approve"
            >
              {cs.proposals.approve}
            </Btn>
            <Btn
              variant="danger"
              size="lg"
              disabled={decideMutation.isPending || !canDecide}
              onClick={() => {
                setError(null);
                setConfirming("REJECT");
              }}
              data-testid="proposal-reject"
            >
              {cs.proposals.reject}
            </Btn>
            <Btn size="lg" onClick={() => openJarvis(cs.proposals.discussPrefill(item.title))}>
              {cs.proposals.discuss}
            </Btn>
          </div>
          <div className="mt-3 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.proposals.afterNote}
          </div>
        </>
      )}

      {confirming ? (
        <ConfirmModal
          testId="proposals"
          kicker={cs.proposals.confirmTitle}
          itemTitle={item.title}
          label={confirming === "APPROVE" ? cs.proposals.confirmApprove : cs.proposals.confirmReject}
          body={note.trim() || undefined}
          danger={confirming === "REJECT"}
          pending={decideMutation.isPending}
          afterNote={cs.proposals.afterNote}
          onConfirm={() => {
            const which = confirming;
            setConfirming(null);
            decideMutation.mutate(which);
          }}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}
